import { useInfiniteQuery, UseInfiniteQueryOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { differenceInMonths, isAfter } from 'date-fns';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { useFlag } from '~/components/Flag';
import { LandType, ZoneType } from '~/components/Land/Land.hooks';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useSearchParams } from '~/components/Route';
import { sharedDateTimer } from '~/components/Util/timer.hooks';

export type LandHistorySummaryCategory = 'ALL' | 'ONGOING' | 'WINS' | 'NOT_WINS';

export type LandHistoryCategory = LandHistorySummaryCategory | 'REFUNDED';

export type LandHistoriesParams = {
  category?: LandHistoryCategory;
  pageNo?: number;
};

export const schema = yup.object().shape({
  category: yup.string().oneOf(['ALL', 'ONGOING', 'WINS', 'NOT_WINS']).default('ALL'),
  page: yup.number(),
});
export const useLandHistoryParams = () => {
  const converter = useCallback((params) => schema.castOrDefault(params), []);
  return useSearchParams<LandHistoriesParams>({ converter });
};

export type LandHistory = {
  week: number;
  landType: LandType;
  zoneType: ZoneType;
  zoneNumber: number; // transformed data;
  snapshotLup: number;
  snapshotNftLevel: number;
  snapshotNftCount: number;
  participationAt: Date; // transformed data;
  category: LandHistoryCategory;
  rewardAmountInFnsa?: number;
  rewardAmountInUsd?: number;
  updatedAt: Date; // transformed data;
};

export type LandHistoriesResponse = {
  histories: LandHistory[];
  pageNo: number;
  pageSize: number;
  isLast: boolean;
};

export const landHistoriesUrl = '/api/citizen/v1/lands/my-history';
export const getLandHistories = async (params: LandHistoriesParams) => {
  const { data } = await axios.get<LandHistoriesResponse>(landHistoriesUrl, { params });
  return {
    ...data,
    histories: data.histories.map((landHistory) => ({
      ...landHistory,
      zoneNumber: Number(landHistory.zoneType.split('_')[1]),
      participationAt: new Date(landHistory.participationAt),
      updatedAt: new Date(landHistory.updatedAt),
    })),
  };
};

export const useLandHistories = (
  params?: LandHistoriesParams,
  options?: UseInfiniteQueryOptions<LandHistoriesResponse>
) => {
  const category = params?.category ?? 'ALL';
  return useInfiniteQuery({
    queryKey: ['land', 'history', { category }],
    queryFn: ({ pageParam: page }) => getLandHistories({ ...params, category, pageNo: page }),
    getNextPageParam: ({ pageNo, isLast }) => (isLast ? undefined : pageNo + 1),
    getPreviousPageParam: ({ pageNo }) => (pageNo < 1 ? undefined : pageNo - 1),
    ...options,
  });
};

export const useLandHistoryLastVisitedDate = () => {
  const { value, setValue } = useFlag({ name: 'LAND_HISTORY_LAST_VISIT' });

  const visit = useCallback(() => setValue(sharedDateTimer.now.getTime()), [setValue]);

  const lastVisit = useMemo(() => {
    if (typeof value !== 'number') return new Date(0);
    return new Date(value);
  }, [value]);

  return {
    value: lastVisit,
    visit,
  };
};

export const landHistorySummaryUrl = '/api/citizen/v1/lands/my-history/common';

export type LandHistorySummary = {
  totalRewardAmountInFnsa: number;
  counts: Record<'all' | 'ongoing' | 'wins' | 'notWins', number>;
};

export const getLandHistorySummary = async () => {
  const { data } = await axios.get<LandHistorySummary>(landHistorySummaryUrl);
  return data;
};
export const useLandHistorySummary = (options?: UseDosiminQueryOptions<LandHistorySummary>) =>
  useQuery<LandHistorySummary>({
    queryKey: ['land', 'history', 'summary'],
    queryFn: getLandHistorySummary,
    initialData: {
      totalRewardAmountInFnsa: 0,
      counts: {
        all: 0,
        ongoing: 0,
        wins: 0,
        notWins: 0,
      },
    },
    ...options,
  });

export const useNewWinningLandHistory = () => {
  const { value, setValue } = useFlag({ name: 'LAST_WIN_LAND_HISTORY_UPDATED_AT' });
  const lastUpdatedAt = useMemo(() => {
    if (typeof value !== 'number') return new Date(0);
    return new Date(value);
  }, [value]);

  const { data: { pages = [] } = {} } = useLandHistories(
    { category: 'ALL' },
    {
      refetchOnWindowFocus: false,
    }
  );
  const latestWinning = useMemo(
    () => pages[0]?.histories.find((history) => history.category === 'WINS'),
    [pages]
  );

  const newWinningLandHistory = useMemo(() => {
    if (!latestWinning) return undefined;
    if (differenceInMonths(sharedDateTimer.now, latestWinning.updatedAt) > 0) return undefined;
    return isAfter(latestWinning.updatedAt, lastUpdatedAt) ? latestWinning : undefined;
  }, [lastUpdatedAt, latestWinning]);

  const update = useCallback(() => {
    if (!newWinningLandHistory) return;
    setValue(newWinningLandHistory.updatedAt.getTime());
  }, [newWinningLandHistory, setValue]);

  return {
    value: newWinningLandHistory,
    update,
  };
};
